import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { toast } from "react-toastify";
import api from "../api";

const formatWhen = (iso) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";

/**
 * Parent / student Messages tab: a copy of every email we sent this account, readable in the
 * portal. The email is shown in a sandboxed frame (no scripts) exactly as it was sent.
 */
export default function PanelMessages({ apiPrefix }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${apiPrefix}/messages`, { params: { per_page: 50 } });
      setItems(res.data?.data || []);
      setUnreadCount(res.data?.meta?.unread_count ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load messages.");
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => { load(); }, [load]);

  const openMessage = async (row) => {
    setOpening(true);
    try {
      const res = await api.get(`${apiPrefix}/messages/${row.id}`);
      setOpen(res.data?.data || null);
      if (!row.read_at) {
        await api.post(`${apiPrefix}/messages/${row.id}/mark-as-read`);
        setItems((prev) => prev.map((m) => (m.id === row.id ? { ...m, read_at: new Date().toISOString() } : m)));
        setUnreadCount((c) => Math.max(0, c - 1));
        window.dispatchEvent(new CustomEvent("panel-messages-read"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not open this message.");
    } finally {
      setOpening(false);
    }
  };

  const markAll = async () => {
    try {
      await api.post(`${apiPrefix}/messages/mark-all-read`);
      setItems((prev) => prev.map((m) => ({ ...m, read_at: m.read_at || new Date().toISOString() })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent("panel-messages-read"));
    } catch {
      toast.error("Could not mark messages as read.");
    }
  };

  if (open) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setOpen(null)} sx={{ mb: 2, textTransform: "none" }}>
          All messages
        </Button>
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700}>{open.subject}</Typography>
            <Typography variant="caption" color="text.secondary">{formatWhen(open.sent_at)}</Typography>
          </Box>
          {open.html_body ? (
            <Box
              component="iframe"
              title={open.subject}
              sandbox=""
              srcDoc={open.html_body}
              sx={{ width: "100%", minHeight: "70vh", border: 0, display: "block", bgcolor: "#fff" }}
            />
          ) : (
            <Typography sx={{ p: 2, whiteSpace: "pre-wrap" }}>{open.text_body}</Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Messages</Typography>
          <Typography variant="body2" color="text.secondary">
            Every email we have sent you, so you can read it here too.
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <Button variant="outlined" size="small" onClick={markAll} sx={{ textTransform: "none" }}>
            Mark all read ({unreadCount})
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <MailOutlineIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography color="text.secondary">No messages yet. Emails we send you will appear here.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((m, i) => (
              <React.Fragment key={m.id}>
                {i > 0 && <Divider component="li" />}
                <ListItemButton onClick={() => openMessage(m)} disabled={opening} sx={{ alignItems: "flex-start", gap: 1.5, py: 1.5 }}>
                  <Box
                    aria-label={m.read_at ? "Read" : "Unread"}
                    sx={{ width: 8, height: 8, borderRadius: "50%", mt: 1, flexShrink: 0, bgcolor: m.read_at ? "transparent" : "primary.main" }}
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="body1" fontWeight={m.read_at ? 500 : 700} noWrap>{m.subject}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{formatWhen(m.sent_at)}</Typography>
                      </Box>
                    }
                    secondary={m.snippet}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
